import { useParams, Link } from "react-router-dom";
import useBlogPosts from "@/hooks/useBlogPosts";
import SharePost from "@/pages/blog/components/SharePost";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import HealthDisclaimer from "@/components/base/HealthDisclaimer";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getPostBySlug, publishedPosts, loaded } = useBlogPosts();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!loaded) {
    return (
      <main className="min-h-screen pt-24 md:pt-28 pb-16">
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen pt-24 md:pt-28 pb-16">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center py-20">
            <p className="font-body text-foreground-500 text-sm mb-4">
              This post could not be found.
            </p>
            <Link
              to="/blog"
              className="inline-block font-body text-sm text-primary-600 hover:text-primary-700 transition-colors duration-300"
            >
              &larr; Back to blog
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const formattedDate = new Date(post.createdAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const formattedUpdated = post.updatedAt !== post.createdAt
    ? new Date(post.updatedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const postSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url: `https://serenasquietrecoveryspace.com/blog/${post.slug}`,
    image: post.coverImage,
    datePublished: post.createdAt,
    dateModified: post.updatedAt,
    author: {
      "@type": "Person",
      name: "Serena Raimes",
      jobTitle: "Recovery Coach",
      url: "https://serenasquietrecoveryspace.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Serena's Quiet Recovery Space",
      url: "https://serenasquietrecoveryspace.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://serenasquietrecoveryspace.com/blog/${post.slug}`,
    },
  };

  return (
    <main className="min-h-screen pt-24 md:pt-28 pb-16 md:pb-20">
      <SEOMeta
        title={`${post.title} | Serena's Quiet Recovery Space Blog`}
        description={post.excerpt}
        canonicalUrl={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.coverImage}
        schema={postSchema}
      />

      <article className="w-full px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Blog", href: "/blog" },
              { label: post.title },
            ]}
          />
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 font-body text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-300 mb-8"
          >
            <i className="ri-arrow-left-line text-sm" />
            Back to blog
          </Link>

          {post.coverImage && (
            <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={post.coverImage}
                alt={post.title}
                title={`${post.title} - Serena's Quiet Recovery Space Blog`}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block bg-accent-100 text-accent-800 font-label text-xs font-medium px-2.5 py-1 rounded-full">
                {post.category}
              </span>
              <span className="font-body text-xs text-foreground-500">Published {formattedDate}</span>
              {formattedUpdated && (
                <>
                  <span className="font-body text-xs text-foreground-400">&middot;</span>
                  <span className="font-body text-xs text-foreground-500">Updated {formattedUpdated}</span>
                </>
              )}
              <span className="font-body text-xs text-foreground-400">&middot;</span>
              <span className="font-body text-xs text-foreground-500">By Serena Raimes</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-light text-foreground-950 mb-4 leading-tight">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="font-body text-base md:text-lg text-foreground-600 leading-relaxed">
                {post.excerpt}
              </p>
            )}
          </header>

          <div
            className="blog-content prose-custom font-body text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block bg-background-100 text-foreground-600 font-label text-xs px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <SharePost title={post.title} slug={post.slug} />
              </div>
            </div>
          )}

          <div className="mt-14 bg-background-100 rounded-lg p-6 md:p-8 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-medium text-foreground-950 mb-2">
              Ready to take your next step?
            </h3>
            <p className="font-body text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Personalised coaching can help you move forward with calm, clarity, and confidence.
            </p>
            <Link
              to="/support"
              className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Explore coaching
            </Link>
          </div>

          <div className="mt-8">
            <HealthDisclaimer variant="box" />
          </div>
        </div>
      </article>

      <style>{`
        .blog-content h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 500;
          color: oklch(var(--foreground-950));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.35;
        }
        .blog-content h3 {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 500;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .blog-content p {
          margin-bottom: 1.25rem;
          line-height: 1.8;
          font-size: 1rem;
        }
        .blog-content ul, .blog-content ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .blog-content li {
          margin-bottom: 0.5rem;
          line-height: 1.7;
        }
        .blog-content ul li {
          list-style-type: disc;
        }
        .blog-content ol li {
          list-style-type: decimal;
        }
        .blog-content a {
          color: oklch(var(--primary-600));
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .blog-content a:hover {
          color: oklch(var(--primary-700));
        }
        .blog-content strong {
          font-weight: 600;
          color: oklch(var(--foreground-950));
        }
        .blog-content blockquote {
          border-left: 3px solid oklch(var(--primary-300));
          padding-left: 1.25rem;
          margin: 1.5rem 0;
          color: oklch(var(--foreground-600));
          font-style: italic;
        }
        @media (min-width: 768px) {
          .blog-content h2 { font-size: 1.75rem; }
          .blog-content p { font-size: 1.0625rem; }
        }
      `}</style>
    </main>
  );
}